import { createContext, useContext, useState, useCallback } from 'react';

// Langue de l'espace management — indépendante de la langue du site public
// (siteLang.js) : une équipe qui gère winston.ie peut vouloir travailler en
// anglais même si le site public s'affiche en français, et vice versa.
// Persistée localement, FR/EN uniquement (pas de VI ici — outil interne).
const STORAGE_KEY = 'winston_admin_lang';

const AdminLangContext = createContext({ lang: 'fr', setLang: () => { }, toggleLang: () => { } });

function readInitialLang() {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY);
        return saved === 'en' ? 'en' : 'fr';
    } catch {
        return 'fr';
    }
}

export function AdminLangProvider({ children }) {
    const [lang, setLangState] = useState(readInitialLang);

    const setLang = useCallback((next) => {
        setLangState(next);
        try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
    }, []);

    const toggleLang = useCallback(() => {
        setLangState((prev) => {
            const next = prev === 'fr' ? 'en' : 'fr';
            try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* noop */ }
            return next;
        });
    }, []);

    return (
        <AdminLangContext.Provider value={{ lang, setLang, toggleLang }}>
            {children}
        </AdminLangContext.Provider>
    );
}

// Petit switch FR/EN réutilisable — entièrement en style inline (aucune
// classe externe requise) pour ne pas dépendre d'un .w-lang-toggle qui
// pourrait ne pas exister sur le CSS actuellement déployé. Couleur reprise
// du texte parent (currentColor) pour s'adapter au fond clair ou sombre.
export function AdminLangSwitch() {
    const { lang, setLang } = useContext(AdminLangContext);
    const itemStyle = (active) => ({
        background: 'none',
        border: 'none',
        padding: '2px 0',
        fontFamily: "'Helvetica Neue', Arial, sans-serif",
        fontSize: '11px',
        fontWeight: active ? 700 : 500,
        color: 'currentColor',
        opacity: active ? 1 : 0.45,
        cursor: 'pointer',
    });
    return (
        <div role="group" aria-label="Langue / Language" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button style={itemStyle(lang === 'fr')} onClick={() => setLang('fr')}>FR</button>
            <span style={{ opacity: 0.3, fontSize: '11px' }}>/</span>
            <button style={itemStyle(lang === 'en')} onClick={() => setLang('en')}>EN</button>
        </div>
    );
}

export function useAdminLang() {
    return useContext(AdminLangContext);
}